import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { areasAPI } from '../../utils/api';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Loader from '../../components/Loader/Loader';
import { SkeletonCard } from '../../components/Skeleton/Skeleton';
import '../../components/Skeleton/Skeleton.css';
import './Dashboard.css';

interface Area {
  id: number;
  name: string;
  description: string | null;
  cameras_count?: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Форма создания
  const [areaName, setAreaName] = useState('');
  const [areaDescription, setAreaDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Модальное окно редактирования
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  // Модальное окно удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingArea, setDeletingArea] = useState<Area | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Загрузка участков
  useEffect(() => {
    loadAreas();
  }, []);

  const loadAreas = async () => {
    try {
      setLoading(true);
      const response = await areasAPI.getAll();
      if (response.success) {
        setAreas(response.areas);
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки участков');
    } finally {
      setLoading(false);
    }
  };

  // Создание участка
  const handleCreateArea = async (e: FormEvent) => {
    e.preventDefault();
    if (!areaName.trim()) {
      setError('Введите название участка');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      const response = await areasAPI.create(areaName.trim(), areaDescription.trim() || undefined);
      if (response.success) {
        setAreaName('');
        setAreaDescription('');
        await loadAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка создания участка');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Открытие модального окна редактирования
  const handleEditClick = (area: Area) => {
    setEditingArea(area);
    setEditName(area.name);
    setEditDescription(area.description || '');
    setIsEditModalOpen(true);
  };

  // Сохранение изменений
  const handleUpdateArea = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingArea || !editName.trim()) {
      setError('Введите название участка');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      const response = await areasAPI.update(
        editingArea.id,
        editName.trim(),
        editDescription.trim() || undefined
      );
      if (response.success) {
        setIsEditModalOpen(false);
        setEditingArea(null);
        await loadAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка обновления участка');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Открытие модального окна удаления
  const handleDeleteClick = (area: Area) => {
    setDeletingArea(area);
    setIsDeleteModalOpen(true);
  };

  // Подтверждение удаления участка
  const handleConfirmDelete = async () => {
    if (!deletingArea) return;

    try {
      setIsDeleting(true);
      setError('');
      const response = await areasAPI.delete(deletingArea.id);
      if (response.success) {
        setIsDeleteModalOpen(false);
        setDeletingArea(null);
        await loadAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка удаления участка');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="dashboard">
      <Header />
      <main className="dashboard-main container">
        <section className="dashboard-welcome">
          <h1 className="dashboard-welcome__title">
            <span className="dashboard-welcome-title-span">Добро пожаловать,</span>
            <br />
            {user ? (
              user.name
            ) : (
              <span className="dashboard-welcome__name-skeleton">
                <div className="skeleton" style={{ width: '150px', height: '50px', borderRadius: '8px' }}></div>
              </span>
            )}
          </h1>
          <p className="dashboard-welcome__text">
            Создайте участок, добавляйте камеры, выбирайте необходимые аналитики,
            отслеживайте результаты
          </p>
        </section>

        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Создать участок группы камер</h2>

          {error && (
            <div style={{ color: '#ff4444', padding: '10px', marginBottom: '10px', fontSize: '14px' }}>
              {error}
            </div>
          )}

          <form className="dashboard-card__form" onSubmit={handleCreateArea}>
            <div className="dashboard-card__field">
              <input
                type="text"
                className="dashboard-input"
                placeholder="Название участка"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                required
              />
            </div>

            <div className="dashboard-card__field">
              <input
                type="text"
                className="dashboard-input"
                placeholder="Описание"
                value={areaDescription}
                onChange={(e) => setAreaDescription(e.target.value)}
              />
            </div>

            <button type="submit" className="dashboard-button" disabled={isSubmitting}>
              <span className="dashboard-button__icon">+</span>
              <span>{isSubmitting ? 'Создание...' : 'Добавить участок'}</span>
            </button>
          </form>
        </section>
      </main>

      <section className="dashboard-areas">
        <div className="container">
          <h2 className="dashboard-areas__title">Участки с камерами</h2>

          {loading ? (
            <div className="dashboard-areas__grid">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : areas.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6c6c70' }}>
              Участки не найдены. Создайте первый участок выше.
            </div>
          ) : (
            <div className="dashboard-areas__grid">
              {areas.map((area) => (
                <article key={area.id} className="area-card">
                  <header className="area-card__header">
                    <h3 className="area-card__title">{area.name}</h3>
                    <button
                      className="area-card__icon-button"
                      type="button"
                      aria-label="Редактировать участок"
                      onClick={() => handleEditClick(area)}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/edit.svg`}
                        alt=""
                        className="area-card__icon-image"
                      />
                    </button>
                  </header>

                  {area.description && (
                    <p className="area-card__description" style={{ fontSize: '14px', color: '#6c6c70', margin: '8px 0' }}>
                      {area.description}
                    </p>
                  )}

                  <p className="area-card__cameras">
                    <span className="area-card__cameras-label">Камеры:</span>{' '}
                    {area.cameras_count || 0}
                  </p>

                  <div className="area-card__footer">
                    <button
                      className="area-card__open"
                      type="button"
                      onClick={() => navigate(`/area/${area.id}`)}
                    >
                      Открыть участок
                    </button>
                    <button
                      className="area-card__icon-button area-card__icon-button--danger"
                      type="button"
                      aria-label="Удалить участок"
                      onClick={() => handleDeleteClick(area)}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/trash.svg`}
                        alt=""
                        className="area-card__icon-image"
                      />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Модальное окно редактирования */}
      {isEditModalOpen && editingArea && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setIsEditModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '24px', fontWeight: 600 }}>
              Редактировать участок
            </h2>

            {error && (
              <div style={{ color: '#ff4444', padding: '10px', marginBottom: '10px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleUpdateArea}>
              <div style={{ marginBottom: '16px' }}>
                <input
                  type="text"
                  className="dashboard-input"
                  placeholder="Название участка"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <input
                  type="text"
                  className="dashboard-input"
                  placeholder="Описание"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <button
                type="submit"
                className="dashboard-button"
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                {isSubmitting ? 'Сохранение...' : 'Сохранить'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {isDeleteModalOpen && deletingArea && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !isDeleting && setIsDeleteModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 30px 80px rgba(15, 23, 42, 0.12)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 600, color: '#1c1c1e' }}>
              Подтвердите действие
            </h2>
            
            <p style={{ marginBottom: '16px', fontSize: '16px', color: '#6c6c70', lineHeight: '1.5' }}>
              Вы уверены, что хотите удалить участок <strong style={{ color: '#1c1c1e' }}>"{deletingArea.name}"</strong>?
            </p>
            <div
              style={{
                marginBottom: '24px',
                padding: '12px 14px',
                borderRadius: '12px',
                backgroundColor: '#fff7ed',
                border: '1px solid #fed7aa',
                fontSize: '15px',
                color: '#9a3412',
                lineHeight: '1.5',
              }}
            >
              <strong style={{ display: 'block', marginBottom: '6px' }}>Внимание</strong>
              Все камеры этого участка будут удалены безвозвратно.
              {(deletingArea.cameras_count ?? 0) > 0 && (
                <span style={{ display: 'block', marginTop: '8px' }}>
                  Сейчас в участке камер: <strong>{deletingArea.cameras_count}</strong>.
                </span>
              )}
            </div>
            <p style={{ marginBottom: '32px', fontSize: '14px', color: '#6c6c70', lineHeight: '1.5' }}>
              Это действие нельзя отменить.
            </p>

            {error && (
              <div style={{ color: '#ff4444', padding: '10px', marginBottom: '16px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              style={{
                width: '100%',
                height: '48px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: isDeleting ? '#6b7280' : '#dc2626',
                color: 'white',
                fontSize: '15px',
                fontWeight: 500,
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.backgroundColor = '#b91c1c';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                }
              }}
            >
              {isDeleting ? 'Удаление...' : 'Удалить'}
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Dashboard;
